import type {Relation, RemoveOptions} from "typeorm";
import {Column, Entity, ManyToOne, OneToMany, RelationId} from "typeorm";
import {Team} from '@/modules/team/entity';
import type {LineupEnum} from "@/modules/base/enums";
import {PositionAreaEnum, PositionEnum} from "@/modules/base/enums";
import {Max, MaxLength, Min} from "class-validator";
import {Country} from "@/modules/base/entity/Country";
import {BaseEntity} from "@/modules/base/entity";
import {Sortable} from "@/modules/base/interfaces";
import {CanNegotiateInterface} from "@/modules/diplomacy/interfaces";
import {Negotiation} from "@/modules/diplomacy/entity";
import {TransferTeamNegotiation} from "@/modules/transfer-market/entity";
import {Suspension} from "@/modules/competitions/entity";

// todo implement base player entity for all players
@Entity()
export class Player extends BaseEntity implements Sortable, CanNegotiateInterface {
  @ManyToOne(() => Country, {eager: true})
  country: Relation<Country>;

  @Column({type: 'varchar', length: '50'})
  @MaxLength(50)
  name: string;

  @Column({type: 'integer'})
  @Min(1)
  @Max(150)
  talent: number;

  @Column({type: 'integer'})
  @Min(1)
  @Max(150)
  skill: number;

  @Column({type: 'integer'})
  @Min(15)
  @Max(40)
  age: number;

  @Column({type: 'varchar', enum: PositionEnum})
  position: PositionEnum;

  @Column({type: 'integer', nullable: true})
  lineup: LineupEnum | null = null;

  @ManyToOne(() => Team, (Team) => Team.players)
  team?: Relation<Team> | null;

  @RelationId((m: Player) => m.team)
  teamId: number;

  @OneToMany(() => Suspension, r => r.player)
  suspensions: Relation<Suspension[]>;

  @OneToMany(() => TransferTeamNegotiation, r => r.target)
  negotiations: Relation<Negotiation[]>;

  @Column({type: 'float'})
  @Min(0)
  @Max(100)
  revelation = 0;

  @Column({type: 'integer', unsigned: true})
  @Min(0)
  patience: number;

  @Column({type: 'integer', unsigned: true})
  @Min(0)
  greediness: number;

  @Column({type: 'integer', unsigned: true})
  @Min(0)
  resentfulness: number;

  // mark this player as transferred this season at it is only possible to transfer player once a season
  @Column({type: 'boolean'})
  transferred = false;

  @Column({type: 'integer'})
  @Min(1)
  @Max(100)
  baseForm = 50;

  @Column({type: 'integer'})
  @Min(1)
  @Max(100)
  form = 50;

  @Column({type: 'date', nullable: true})
  nextFormChangeAt?: Date;

  @Column({type: 'float'})
  @Min(1)
  @Max(100)
  energy = 100;

  @Column({type: 'integer', nullable: true})
  @Min(1)
  contractYears?: number | null;

  @Column({type: 'integer'})
  @Min(1)
  wage: number;

  @Column({type: 'integer'})
  @Min(1)
  marketValue: number;

  @Column({type: 'boolean'})
  endsCareer = false;

  @Column({type: 'date', nullable: true})
  careerEndedAt?: Date;

  get order() {
    return Object.values(PositionEnum).indexOf(this.position) * 1000 - this.skill;
  }

  get positionArea(): PositionAreaEnum {
    return {
      [PositionEnum.GK]: PositionAreaEnum.GK,
      [PositionEnum.LD]: PositionAreaEnum.DF,
      [PositionEnum.CD]: PositionAreaEnum.DF,
      [PositionEnum.RD]: PositionAreaEnum.DF,
      [PositionEnum.LM]: PositionAreaEnum.MF,
      [PositionEnum.DM]: PositionAreaEnum.MF,
      [PositionEnum.CM]: PositionAreaEnum.MF,
      [PositionEnum.OM]: PositionAreaEnum.MF,
      [PositionEnum.RM]: PositionAreaEnum.MF,
      [PositionEnum.LW]: PositionAreaEnum.AT,
      [PositionEnum.FW]: PositionAreaEnum.AT,
      [PositionEnum.RW]: PositionAreaEnum.AT,
    }[this.position];
  }

  get level() {
    return Math.floor(this.skill / 10);
  }

  get shortName() {
    const lastName = this.name.split(' ').pop();

    return `${this.name[0]}. ${lastName}`;
  }

  get reducedSkill() {
    return Math.round(this.skill / 2);
  }

  get revealedTalent() {
    if (!this.canSee('talent')) {
      return null;
    }

    return this.talent;
  }

  get revealedSkill() {
    return this.skill;
  }

  get image(): string {
    return `/faces/face-1.png`;
  }

  canSee(attr: keyof Player): boolean {
    switch (attr) {
      case 'country':
        return this.revelation >= 10;
      case 'name':
        return this.revelation >= 10;
      case 'age':
        return this.revelation >= 25;
      case 'position':
        return this.revelation >= 50;
      case 'skill':
        return this.revelation >= 75;
      case 'talent':
        return this.revelation >= 100;
    }

    return true;
  }
}
